import type { StyleRow } from './stylesFixtureData';
import { seasons, groups, brandOptions, suppliers, contactPersons } from './stylesFixtureData';

/**
 * Fixtures for the DL-66630 epic (dl-57695, Testmo folder 93168, part 4 of 9) — the "Last Updated"
 * date filter (CAST AS DATE semantics, open-ended From/To bounds, inclusive range boundaries).
 *
 * Additive only (CLAUDE.md §4 / the mock sibling of `no-new-sprocs.md`): every row here is appended
 * to `stylesData` on top of the existing generated + bulk-fixture rows (ids 97001-97040, an unused
 * range — see `styles.ts` `buildDefaultStylesData`). Nothing existing is edited.
 *
 * The existing `lastupdated` mock filter (`styles.ts` `applyFilters`) already implements the
 * semantics this epic tests: `day = r.lastUpdated.slice(0, 10)` compares calendar-day strings only
 * (so any clock time on a given day collapses to the same key — the CAST AS DATE / time-strips
 * claim is true by construction), and `(!from || day >= from) && (!to || day <= to)` treats an
 * absent bound as genuinely open-ended (the sentinel/legacy-list claims resolve to "the frontend
 * never constructs a 9999-12-31 literal or a single-element list at all" — see
 * `modules/table/components/filter/transforms.ts` `getValueForUrl`, which always serializes a
 * `dateRange` filter as `<from>,<to>` with an empty side, never a sentinel or a bare single date).
 * These fixtures exist purely to give that already-correct filter real boundary rows to prove
 * itself against, at millisecond precision the deterministic `generateStyle` seed (fixed at
 * `T10:00:00` per row) cannot provide.
 *
 * Four fixture groups, one per DL-66630 spec file (`e2e/src/pages/styles/styles-last-updated-*`):
 *
 * - **Same-day** (ids 97001-97011, target date 2026-03-10) — DL-66632/66636/66640/66642.
 * - **Open-ended** (ids 97012-97022, From boundary 2026-01-01 / To boundary 2026-02-28) —
 *   DL-66633/66634/66637/66638/66639.
 * - **Range** (ids 97023-97036, From 2026-01-01, two upper bounds 2026-03-27 / 2026-03-31) —
 *   DL-66631/66641.
 * - **Clear-filter brand pairing** (ids 97037-97040, two distinct real brands) — DL-66635.
 */

let nextFixtureId = 97001;
const nextId = (): number => nextFixtureId++;

const baseLastUpdatedFixtureRow = (overrides: Partial<StyleRow> & { name: string }): StyleRow => ({
  id: nextId(),
  customId: `ST-${nextFixtureId - 1}`,
  description: null,
  state: 'published',
  logo: '',
  season: seasons[0],
  group: groups[0],
  brand: {
    id: brandOptions[0].id,
    customId: brandOptions[0].userDefinedId ?? '',
    name: brandOptions[0].name,
    contactPersonId: contactPersons[0].id,
    contactPersonName: contactPersons[0].name,
  },
  supplier: {
    id: suppliers[0].id,
    customId: suppliers[0].customId,
    name: suppliers[0].name,
    contactPersonId: contactPersons[1].id,
    contactPersonName: contactPersons[1].name,
  },
  categories: [],
  styleColors: [],
  customFields: [],
  properties: [],
  lastUpdated: '2026-01-01T00:00:00',
  readyForExport: false,
  ...overrides,
});

// ============================================================================
// Same-day group — target date 2026-03-10 (DL-66632, DL-66636, DL-66640, DL-66642)
// ============================================================================

/** Row-name constants so specs resolve rows by identity, never by index. */
export const SAME_DAY_NAMES = {
  prevDayGuard: 'LU SameDay Prev Day Guard',
  startOfDay: 'LU SameDay Start Of Day',
  earlyMorning: 'LU SameDay Early Morning',
  morning: 'LU SameDay Morning',
  midday: 'LU SameDay Midday',
  evening: 'LU SameDay Evening',
  lateNight: 'LU SameDay Late Night',
  endOfDaySecond: 'LU SameDay End Of Day Whole Second',
  endOfDaySubSecond: 'LU SameDay End Of Day Sub Second',
  endOfDayMaxPrecision: 'LU SameDay End Of Day Max Precision',
  nextDayGuard: 'LU SameDay Next Day Guard',
} as const;

/** Names of the 9 rows genuinely on 2026-03-10 (excludes the two adjacent-day guards). */
export const SAME_DAY_INCLUDED_NAMES = [
  SAME_DAY_NAMES.startOfDay,
  SAME_DAY_NAMES.earlyMorning,
  SAME_DAY_NAMES.morning,
  SAME_DAY_NAMES.midday,
  SAME_DAY_NAMES.evening,
  SAME_DAY_NAMES.lateNight,
  SAME_DAY_NAMES.endOfDaySecond,
  SAME_DAY_NAMES.endOfDaySubSecond,
  SAME_DAY_NAMES.endOfDayMaxPrecision,
];

export const SAME_DAY_EXCLUDED_NAMES = [SAME_DAY_NAMES.prevDayGuard, SAME_DAY_NAMES.nextDayGuard];

const buildSameDayFixtureStyles = (): StyleRow[] => [
  baseLastUpdatedFixtureRow({
    name: SAME_DAY_NAMES.prevDayGuard,
    lastUpdated: '2026-03-09T23:59:59.999',
  }),
  baseLastUpdatedFixtureRow({
    name: SAME_DAY_NAMES.startOfDay,
    lastUpdated: '2026-03-10T00:00:00.000',
  }),
  baseLastUpdatedFixtureRow({
    name: SAME_DAY_NAMES.earlyMorning,
    lastUpdated: '2026-03-10T00:01:00.000',
  }),
  baseLastUpdatedFixtureRow({
    name: SAME_DAY_NAMES.morning,
    lastUpdated: '2026-03-10T06:30:00.000',
  }),
  baseLastUpdatedFixtureRow({
    name: SAME_DAY_NAMES.midday,
    lastUpdated: '2026-03-10T12:00:00.000',
  }),
  baseLastUpdatedFixtureRow({
    name: SAME_DAY_NAMES.evening,
    lastUpdated: '2026-03-10T18:45:00.000',
  }),
  baseLastUpdatedFixtureRow({
    name: SAME_DAY_NAMES.lateNight,
    lastUpdated: '2026-03-10T23:58:00.000',
  }),
  baseLastUpdatedFixtureRow({
    name: SAME_DAY_NAMES.endOfDaySecond,
    lastUpdated: '2026-03-10T23:59:59.000',
  }),
  baseLastUpdatedFixtureRow({
    name: SAME_DAY_NAMES.endOfDaySubSecond,
    lastUpdated: '2026-03-10T23:59:59.997',
  }),
  baseLastUpdatedFixtureRow({
    name: SAME_DAY_NAMES.endOfDayMaxPrecision,
    lastUpdated: '2026-03-10T23:59:59.999',
  }),
  baseLastUpdatedFixtureRow({
    name: SAME_DAY_NAMES.nextDayGuard,
    lastUpdated: '2026-03-11T00:00:00.000',
  }),
];

// ============================================================================
// Open-ended group — From boundary 2026-01-01 / To boundary 2026-02-28
// (DL-66633, DL-66634, DL-66637, DL-66638, DL-66639)
// ============================================================================

export const OPEN_ENDED_NAMES = {
  farPast: 'LU Open Far Past',
  wellBefore: 'LU Open Well Before',
  dayBeforeFromLate: 'LU Open Day Before From Late',
  fromBoundaryStart: 'LU Open From Boundary Start',
  fromBoundaryEnd: 'LU Open From Boundary End',
  midJan: 'LU Open Mid Jan',
  toBoundaryStart: 'LU Open To Boundary Start',
  toBoundaryEnd: 'LU Open To Boundary End',
  dayAfterTo: 'LU Open Day After To',
  wellAfter: 'LU Open Well After',
  farFuture: 'LU Open Far Future',
} as const;

/** Included when From=2026-01-01, To empty (open upper bound). */
export const OPEN_ENDED_FROM_ONLY_INCLUDED_NAMES = [
  OPEN_ENDED_NAMES.fromBoundaryStart,
  OPEN_ENDED_NAMES.fromBoundaryEnd,
  OPEN_ENDED_NAMES.midJan,
  OPEN_ENDED_NAMES.toBoundaryStart,
  OPEN_ENDED_NAMES.toBoundaryEnd,
  OPEN_ENDED_NAMES.dayAfterTo,
  OPEN_ENDED_NAMES.wellAfter,
  OPEN_ENDED_NAMES.farFuture,
];
export const OPEN_ENDED_FROM_ONLY_EXCLUDED_NAMES = [
  OPEN_ENDED_NAMES.farPast,
  OPEN_ENDED_NAMES.wellBefore,
  OPEN_ENDED_NAMES.dayBeforeFromLate,
];

/** Included when To=2026-02-28, From empty (open lower bound). */
export const OPEN_ENDED_TO_ONLY_INCLUDED_NAMES = [
  OPEN_ENDED_NAMES.farPast,
  OPEN_ENDED_NAMES.wellBefore,
  OPEN_ENDED_NAMES.dayBeforeFromLate,
  OPEN_ENDED_NAMES.fromBoundaryStart,
  OPEN_ENDED_NAMES.fromBoundaryEnd,
  OPEN_ENDED_NAMES.midJan,
  OPEN_ENDED_NAMES.toBoundaryStart,
  OPEN_ENDED_NAMES.toBoundaryEnd,
];
export const OPEN_ENDED_TO_ONLY_EXCLUDED_NAMES = [
  OPEN_ENDED_NAMES.dayAfterTo,
  OPEN_ENDED_NAMES.wellAfter,
  OPEN_ENDED_NAMES.farFuture,
];

const buildOpenEndedFixtureStyles = (): StyleRow[] => [
  baseLastUpdatedFixtureRow({ name: OPEN_ENDED_NAMES.farPast, lastUpdated: '2024-05-01T12:00:00' }),
  baseLastUpdatedFixtureRow({
    name: OPEN_ENDED_NAMES.wellBefore,
    lastUpdated: '2025-11-15T12:00:00',
  }),
  baseLastUpdatedFixtureRow({
    name: OPEN_ENDED_NAMES.dayBeforeFromLate,
    lastUpdated: '2025-12-31T23:59:59',
  }),
  baseLastUpdatedFixtureRow({
    name: OPEN_ENDED_NAMES.fromBoundaryStart,
    lastUpdated: '2026-01-01T00:00:00',
  }),
  baseLastUpdatedFixtureRow({
    name: OPEN_ENDED_NAMES.fromBoundaryEnd,
    lastUpdated: '2026-01-01T23:58:00',
  }),
  baseLastUpdatedFixtureRow({ name: OPEN_ENDED_NAMES.midJan, lastUpdated: '2026-01-15T12:00:00' }),
  baseLastUpdatedFixtureRow({
    name: OPEN_ENDED_NAMES.toBoundaryStart,
    lastUpdated: '2026-02-28T00:00:00',
  }),
  baseLastUpdatedFixtureRow({
    name: OPEN_ENDED_NAMES.toBoundaryEnd,
    lastUpdated: '2026-02-28T23:59:59',
  }),
  baseLastUpdatedFixtureRow({
    name: OPEN_ENDED_NAMES.dayAfterTo,
    lastUpdated: '2026-03-01T00:00:00',
  }),
  baseLastUpdatedFixtureRow({
    name: OPEN_ENDED_NAMES.wellAfter,
    lastUpdated: '2026-03-20T12:00:00',
  }),
  baseLastUpdatedFixtureRow({
    name: OPEN_ENDED_NAMES.farFuture,
    lastUpdated: '2027-06-01T00:00:00',
  }),
];

// ============================================================================
// Range group — From 2026-01-01, two upper bounds (2026-03-27 / 2026-03-31)
// (DL-66631, DL-66641)
// ============================================================================

export const RANGE_NAMES = {
  dayBeforeStart: 'LU Range Day Before Start',
  startEarly: 'LU Range Start Early',
  startLate: 'LU Range Start Late',
  janEnd: 'LU Range Jan End',
  febStart: 'LU Range Feb Start',
  febMid: 'LU Range Feb Mid',
  febEnd: 'LU Range Feb End',
  marStart: 'LU Range Mar Start',
  mar27Early: 'LU Range Mar27 Early',
  mar27Late: 'LU Range Mar27 Late',
  mar28: 'LU Range Mar28',
  mar31Early: 'LU Range Mar31 Early',
  mar31Late: 'LU Range Mar31 Late',
  dayAfterEnd: 'LU Range Day After End',
} as const;

/** Included for From=2026-01-01, To=2026-03-27 (DL-66631, Testmo 754911). */
export const RANGE_754911_INCLUDED_NAMES = [
  RANGE_NAMES.startEarly,
  RANGE_NAMES.startLate,
  RANGE_NAMES.janEnd,
  RANGE_NAMES.febStart,
  RANGE_NAMES.febMid,
  RANGE_NAMES.febEnd,
  RANGE_NAMES.marStart,
  RANGE_NAMES.mar27Early,
  RANGE_NAMES.mar27Late,
];
export const RANGE_754911_EXCLUDED_NAMES = [
  RANGE_NAMES.dayBeforeStart,
  RANGE_NAMES.mar28,
  RANGE_NAMES.mar31Early,
  RANGE_NAMES.mar31Late,
  RANGE_NAMES.dayAfterEnd,
];

/** Included for From=2026-01-01, To=2026-03-31 (DL-66641, Testmo 788291). */
export const RANGE_788291_INCLUDED_NAMES = [
  ...RANGE_754911_INCLUDED_NAMES,
  RANGE_NAMES.mar28,
  RANGE_NAMES.mar31Early,
  RANGE_NAMES.mar31Late,
];
export const RANGE_788291_EXCLUDED_NAMES = [RANGE_NAMES.dayBeforeStart, RANGE_NAMES.dayAfterEnd];

const buildRangeFixtureStyles = (): StyleRow[] => [
  baseLastUpdatedFixtureRow({
    name: RANGE_NAMES.dayBeforeStart,
    lastUpdated: '2025-12-31T23:59:59',
  }),
  baseLastUpdatedFixtureRow({ name: RANGE_NAMES.startEarly, lastUpdated: '2026-01-01T00:00:00' }),
  baseLastUpdatedFixtureRow({ name: RANGE_NAMES.startLate, lastUpdated: '2026-01-01T23:59:59' }),
  baseLastUpdatedFixtureRow({ name: RANGE_NAMES.janEnd, lastUpdated: '2026-01-31T12:00:00' }),
  baseLastUpdatedFixtureRow({ name: RANGE_NAMES.febStart, lastUpdated: '2026-02-01T12:00:00' }),
  baseLastUpdatedFixtureRow({ name: RANGE_NAMES.febMid, lastUpdated: '2026-02-15T12:00:00' }),
  baseLastUpdatedFixtureRow({ name: RANGE_NAMES.febEnd, lastUpdated: '2026-02-28T12:00:00' }),
  baseLastUpdatedFixtureRow({ name: RANGE_NAMES.marStart, lastUpdated: '2026-03-01T12:00:00' }),
  baseLastUpdatedFixtureRow({ name: RANGE_NAMES.mar27Early, lastUpdated: '2026-03-27T00:01:00' }),
  baseLastUpdatedFixtureRow({ name: RANGE_NAMES.mar27Late, lastUpdated: '2026-03-27T23:59:59' }),
  baseLastUpdatedFixtureRow({ name: RANGE_NAMES.mar28, lastUpdated: '2026-03-28T00:01:00' }),
  baseLastUpdatedFixtureRow({ name: RANGE_NAMES.mar31Early, lastUpdated: '2026-03-31T00:00:00' }),
  baseLastUpdatedFixtureRow({ name: RANGE_NAMES.mar31Late, lastUpdated: '2026-03-31T23:59:59' }),
  baseLastUpdatedFixtureRow({ name: RANGE_NAMES.dayAfterEnd, lastUpdated: '2026-04-01T00:00:00' }),
];

// ============================================================================
// Clear-filter brand-pairing group (DL-66635) — two distinct real brands so a
// partial-clear regression (dropping the surviving Brand filter, or keeping the
// removed date bound) is provable by identity.
// ============================================================================

export const CLEAR_FILTER_NAMES = {
  brandAInRange: 'LU Clear InRange BrandA',
  brandAOutOfRange: 'LU Clear OutRange BrandA',
  brandBInRange: 'LU Clear InRange BrandB',
  brandBOutOfRange: 'LU Clear OutRange BrandB',
} as const;

/** The shared range used by the DL-66635 spec: From=2026-05-01, To=2026-05-31. */
export const CLEAR_FILTER_RANGE = { from: '2026-05-01', to: '2026-05-31' };

const buildClearFilterFixtureStyles = (): StyleRow[] => [
  baseLastUpdatedFixtureRow({
    name: CLEAR_FILTER_NAMES.brandAInRange,
    lastUpdated: '2026-05-10T09:00:00',
    brand: {
      id: brandOptions[0].id,
      customId: brandOptions[0].userDefinedId ?? '',
      name: brandOptions[0].name,
      contactPersonId: contactPersons[0].id,
      contactPersonName: contactPersons[0].name,
    },
  }),
  baseLastUpdatedFixtureRow({
    name: CLEAR_FILTER_NAMES.brandAOutOfRange,
    lastUpdated: '2026-07-10T09:00:00',
    brand: {
      id: brandOptions[0].id,
      customId: brandOptions[0].userDefinedId ?? '',
      name: brandOptions[0].name,
      contactPersonId: contactPersons[0].id,
      contactPersonName: contactPersons[0].name,
    },
  }),
  baseLastUpdatedFixtureRow({
    name: CLEAR_FILTER_NAMES.brandBInRange,
    lastUpdated: '2026-05-15T09:00:00',
    brand: {
      id: brandOptions[1].id,
      customId: brandOptions[1].userDefinedId ?? '',
      name: brandOptions[1].name,
      contactPersonId: contactPersons[0].id,
      contactPersonName: contactPersons[0].name,
    },
  }),
  baseLastUpdatedFixtureRow({
    name: CLEAR_FILTER_NAMES.brandBOutOfRange,
    lastUpdated: '2026-08-01T09:00:00',
    brand: {
      id: brandOptions[1].id,
      customId: brandOptions[1].userDefinedId ?? '',
      name: brandOptions[1].name,
      contactPersonId: contactPersons[0].id,
      contactPersonName: contactPersons[0].name,
    },
  }),
];

/** All DL-66630 Last Updated fixture rows (ids 97001-97040), appended once to `stylesData`. */
export const buildLastUpdatedFixtureStyles = (): StyleRow[] => [
  ...buildSameDayFixtureStyles(),
  ...buildOpenEndedFixtureStyles(),
  ...buildRangeFixtureStyles(),
  ...buildClearFilterFixtureStyles(),
];
